// ─────────────────────────────────────────────────────────────────────────────
// Bloque de plantilla del armazón de Reportería.
//
// La maqueta lo pinta encima de las SIETE pestañas: «Plantilla actualizada: 05 Jul 2026 · 4 230 total
// · 2 814 Staff 67 % · 1 416 Frontline 33 %». Es lo que más pesa en la impresión de «no se parece»,
// porque se repite en cada pantalla y es lo primero que se ve bajo los filtros.
//
// Vive en el ARMAZÓN y no en un tab porque no depende de la pestaña activa ni del rango de fechas: el
// censo es una foto del directorio. Por eso tiene su propia consulta —`GET …/headcount`, sin
// `from`/`to`— en vez de sacar las cifras del reporte Diario, que sí es del periodo.
//
// 🔴 `total === 0` significa DESCONOCIDO, no «organización vacía»: es lo que devuelve el censo cuando
// no hay snapshots. En ese caso el bloque NO se pinta, en vez de mostrar «0 personas», que se leería
// como un hecho medido. Mismo criterio que las tarjetas de adopción del Diario.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { useRecognitionHeadcount } from '@/api/recognition';
import { AvisoFiltroNoAplica, type FiltroDeTira } from './AvisoFiltroNoAplica';

export interface BloquePlantillaProps {
  readonly ouIds?: readonly string[];
  /**
   * Frase que ata este bloque a lo que viene DEBAJO.
   *
   * 🔴 La maqueta de Brillamos pinta el bloque DOS veces en la misma pantalla. Repetir el mismo texto
   * dos veces sería copia-pega; lo que hace útil la repetición es que cada instancia diga para qué
   * sirven esas cifras ahí: arriba son la referencia del periodo, y la segunda es el DENOMINADOR de
   * las tarjetas de adopción que van justo debajo. Sin `nota` se usa el texto genérico de siempre.
   */
  readonly nota?: string;
  /**
   * Filtros ACTIVOS de la tira, para avisar que el censo no se acota por ellos. El censo es una foto
   * del directorio: ni el Fundamental ni la persona lo recortan. Sin este prop no se pinta ningún
   * aviso — el único llamador que hoy lo necesita es el armazón de `RecognitionReports`.
   */
  readonly filtrosActivos?: readonly FiltroDeTira[];
}

export function BloquePlantilla({ ouIds, nota, filtrosActivos = [] }: BloquePlantillaProps) {
  const { t, i18n } = useTranslation('recognition');
  const { data } = useRecognitionHeadcount({ ouIds });

  // Sin censo no hay bloque. Un «0 personas» sería peor que no decir nada.
  if (!data || data.total <= 0) return null;

  const numero = (n: number) => n.toLocaleString(i18n.language);
  const pct = (n: number) => Math.round((n / data.total) * 100);
  const staff = data.total - data.frontline;

  // La fecha en formato corto del idioma activo. Sin fecha, el rótulo se queda solo con el título:
  // que el censo exista pero no sepamos de cuándo es también es información.
  const actualizado = data.actualizadoEn
    ? new Date(data.actualizadoEn).toLocaleDateString(i18n.language, {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    : null;

  return (
    <div className="rr-plantilla">
      {/* El censo es una foto del directorio: ni el Fundamental ni la persona lo acotan, y la fila
          de cifras no cambiaría al moverlos. Decirlo es lo que evita que se lea como un error. */}
      <AvisoFiltroNoAplica filtros={filtrosActivos} motivo="censo" />
      <div className="rr-plantilla-info">
        <i className="fas fa-id-card-clip rr-plantilla-icon" aria-hidden="true" />
        <div>
          <div className="rr-plantilla-title">
            {actualizado
              ? t('reports.headcount.updatedOn', { fecha: actualizado })
              : t('reports.headcount.title')}
          </div>
          <div className="rr-plantilla-hint">{nota ?? t('reports.headcount.hint')}</div>
        </div>
      </div>

      <div className="rr-plantilla-cifras">
        <div className="rr-plantilla-cifra">
          <div className="rr-plantilla-n">{numero(data.total)}</div>
          <div className="rr-plantilla-l">{t('reports.headcount.total')}</div>
        </div>
        <div className="rr-plantilla-cifra rr-plantilla-cifra--staff">
          <div className="rr-plantilla-n">{numero(staff)}</div>
          <div className="rr-plantilla-l">
            {t('reports.headcount.staff')} <strong>{pct(staff)}%</strong>
          </div>
        </div>
        <div className="rr-plantilla-cifra rr-plantilla-cifra--frontline">
          <div className="rr-plantilla-n">{numero(data.frontline)}</div>
          <div className="rr-plantilla-l">
            {t('reports.headcount.frontline')} <strong>{pct(data.frontline)}%</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
